import React, { useRef, useEffect } from "react";

const GRAVITY = 0.15;
const JUMP_FORCE = -6;
const PIPE_SPEED = 2;
const PIPE_SPACING = 200;

const GameCanvas = ({ onGameOver, onScoreChange }) => {
  const canvasRef = useRef(null);
  const birdRef = useRef({
    y: 200,
    velocity: 0,
  });
  const pipesRef = useRef([]);
  const scoreRef = useRef(0);
  const gameLoopRef = useRef(null);
  const frameCountRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const drawBird = () => {
      ctx.save();
      ctx.translate(100, birdRef.current.y);
      ctx.rotate(birdRef.current.velocity * 0.1);

      // Draw trend arrow
      ctx.fillStyle = "var(--accent-color)";
      ctx.beginPath();

      // Arrow head
      ctx.moveTo(20, 0); // Point
      ctx.lineTo(5, -10); // Top edge
      ctx.lineTo(5, 10); // Bottom edge
      ctx.closePath();
      ctx.fill();

      // Jagged trend line
      ctx.beginPath();
      ctx.lineWidth = 3;
      ctx.strokeStyle = "var(--accent-color)";
      ctx.moveTo(5, 0); // Start at arrow base
      ctx.lineTo(-5, 5); // Down
      ctx.lineTo(-10, -3); // Up
      ctx.lineTo(-15, 4); // Down
      ctx.lineTo(-20, -2); // Up
      ctx.lineTo(-25, 3); // Final down
      ctx.stroke();

      ctx.restore();
    };

    const drawPipe = (pipe) => {
      // Define candlestick properties
      const candleWidth = 40;
      const wickWidth = 4;
      const wickLength = 30;

      // Color based on "candlestick" direction
      const color = pipe.isGreen ? "#10b981" : "#ef4444";
      ctx.fillStyle = color;

      // Draw top candlestick
      // Top wick
      ctx.fillRect(
        pipe.x + (candleWidth - wickWidth) / 2,
        0,
        wickWidth,
        wickLength
      );
      // Main body
      ctx.fillRect(pipe.x, wickLength, candleWidth, pipe.gapStart - wickLength);
      // Bottom wick
      ctx.fillRect(
        pipe.x + (candleWidth - wickWidth) / 2,
        pipe.gapStart - wickLength,
        wickWidth,
        wickLength
      );

      // Draw bottom candlestick
      const bottomStart = pipe.gapStart + PIPE_SPACING;
      // Top wick
      ctx.fillRect(
        pipe.x + (candleWidth - wickWidth) / 2,
        bottomStart,
        wickWidth,
        wickLength
      );
      // Main body
      ctx.fillRect(
        pipe.x,
        bottomStart + wickLength,
        candleWidth,
        canvas.height - (bottomStart + wickLength)
      );
    };

    const update = () => {
      frameCountRef.current++;

      // Update bird
      birdRef.current.velocity += GRAVITY;
      birdRef.current.y += birdRef.current.velocity;

      // Update pipes
      if (frameCountRef.current % 150 === 0) {
        const gapStart =
          Math.random() * (canvas.height - PIPE_SPACING - 100) + 50;
        pipesRef.current.push({
          x: canvas.width,
          gapStart,
          isGreen: Math.random() > 0.5,
          passed: false,
        });
      }

      pipesRef.current.forEach((pipe) => {
        pipe.x -= PIPE_SPEED;

        // Score point when passing pipe
        if (!pipe.passed && pipe.x < 80) {
          pipe.passed = true;
          scoreRef.current++;
          onScoreChange(scoreRef.current);
        }

        // Check collision
        if (
          birdRef.current.y < 0 ||
          birdRef.current.y > canvas.height ||
          (pipe.x < 115 &&
            pipe.x > 85 &&
            (birdRef.current.y < pipe.gapStart ||
              birdRef.current.y > pipe.gapStart + PIPE_SPACING))
        ) {
          cancelAnimationFrame(animationFrameId);
          onGameOver(scoreRef.current);
          return;
        }
      });

      // Remove off-screen pipes
      pipesRef.current = pipesRef.current.filter((pipe) => pipe.x > -40);

      // Draw
      // Fill background white
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      pipesRef.current.forEach(drawPipe);
      drawBird();

      animationFrameId = requestAnimationFrame(update);
    };

    const handleJump = (e) => {
      if (e.code === "Space" || e.type === "click") {
        birdRef.current.velocity = JUMP_FORCE;
      }
    };

    canvas.addEventListener("click", handleJump);
    window.addEventListener("keydown", handleJump);
    animationFrameId = requestAnimationFrame(update);

    return () => {
      canvas.removeEventListener("click", handleJump);
      window.removeEventListener("keydown", handleJump);
      cancelAnimationFrame(animationFrameId);
    };
  }, [onGameOver, onScoreChange]);

  return (
    <canvas
      ref={canvasRef}
      width={800}
      height={600}
      className="game-canvas"
      style={{ backgroundColor: "#FFFFFF" }}
    />
  );
};

export default GameCanvas;
